import { Pagination, useIsMobile } from '@brunommsilva/react-foundations';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountsTable from '../components/accounts/accounts-table';
import CreateAccountForm from '../components/accounts/create-account-form';
import TotalBalance from '../components/accounts/total-balance';
import PageFab from '../components/page-fab/page-fab';
import useAccounts from '../hooks/accounts/use-accounts';
import { AccountStatus } from '../models/accounts';

const AccountsPage = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const filter = { status: AccountStatus.ACTIVE };

  const { accounts, total, isLoading, hasError } = useAccounts(
    filter,
    page,
    perPage
  );

  const changePage = (newPage: number) => {
    setPage(newPage);
  };

  const changePerPage = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleCreateSuccess = () => {
    setIsCreateDialogOpen(false);
  };

  const handleCreateCancel = () => {
    setIsCreateDialogOpen(false);
  };

  const renderCreateAccountFrom = () => {
    return (
      <CreateAccountForm
        onSuccess={handleCreateSuccess}
        onCancel={handleCreateCancel}
      />
    );
  };

  const renderAccounts = () => {
    return (
      <Card variant="outlined">
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('ACCOUNTS')}
            </Typography>
          }
        />
        <CardContent>
          <AccountsTable
            accounts={accounts}
            isLoading={isLoading}
            isError={hasError}
          />
        </CardContent>
        <CardActions>
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Pagination
              page={page}
              perPage={perPage}
              total={total}
              onPageChange={changePage}
              onPerPageChange={changePerPage}
            />
          </Grid>
        </CardActions>
      </Card>
    );
  };

  return (
    <>
      <Grid
        container
        spacing={3}
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid size={12}>
          <TotalBalance />
        </Grid>
        {!isMobile && (
          <Grid
            size={{
              xs: 12,
              md: 4,
              lg: 3,
            }}
          >
            {renderCreateAccountFrom()}
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            md: isMobile ? 12 : 8,
            lg: isMobile ? 12 : 9,
          }}
        >
          {renderAccounts()}
        </Grid>
      </Grid>

      {isMobile && (
        <>
          <PageFab onClick={() => setIsCreateDialogOpen(true)} />
          <Dialog
            open={isCreateDialogOpen}
            onClose={handleCreateCancel}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>{t('CREATE_NEW_ACCOUNT')}</DialogTitle>
            <DialogContent>
              <CreateAccountForm
                isDialog
                onSuccess={handleCreateSuccess}
                onCancel={handleCreateCancel}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
};

export default AccountsPage;
